import { Smartphone, X } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { IOS_APP_STORE_URL, ANDROID_PLAY_STORE_URL, MOBILE_APP_PAUSED } from '../../constants/appStore';

interface MobileAppBannerProps {
  onDismiss?: () => void;
}

/**
 * Mobile App Banner Component
 * Shows a banner on mobile devices suggesting users download/use the RollOps mobile app
 */
export const MobileAppBanner: React.FC<MobileAppBannerProps> = ({ onDismiss }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // Check if user is on mobile device
    const checkMobile = () => {
      // Legacy browser property - opera is not in standard Window type
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
      const isMobileDevice = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
        userAgent.toLowerCase()
      );
      const isSmallScreen = window.innerWidth < 768; // md breakpoint
      
      return isMobileDevice || isSmallScreen;
    };

    // Check if banner was previously dismissed
    const wasDismissed = localStorage.getItem('rollops-mobile-banner-dismissed') === 'true';
    
    setIsMobile(checkMobile());
    setIsVisible(checkMobile() && !wasDismissed);
  }, []);

  const handleDismiss = () => {
    localStorage.setItem('rollops-mobile-banner-dismissed', 'true');
    setIsVisible(false);
    onDismiss?.();
  };

  const handleOpenApp = () => {
    // Build deep link based on current route
    const currentPath = window.location.pathname;
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const scheme = isLocalhost ? 'rollops-dev://' : 'rollops://';
    
    // Try to open the app with the current route
    const deepLink = `${scheme}${currentPath}`;
    
    // Attempt to open app
    window.location.href = deepLink;
    
    // Fallback: if app doesn't open, try after a short delay
    setTimeout(() => {
      // This will only execute if the app didn't open (browser didn't navigate away)
      // App may not be installed, consider showing download options
    }, 500);
  };

  const handleDownloadIOS = () => {
    window.open(IOS_APP_STORE_URL, '_blank');
  };

  const handleDownloadAndroid = () => {
    window.open(ANDROID_PLAY_STORE_URL, '_blank');
  };

  // Phase 1: mobile apps are delisted; never render the install banner regardless
  // of device or dismissal state. See MOBILE_APP_PAUSED in constants/appStore.ts.
  if (MOBILE_APP_PAUSED || !isVisible || !isMobile) {
    return null;
  }

  // Detect iOS vs Android
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isAndroid = /Android/.test(navigator.userAgent);

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-gradient-to-r from-[#9B1421] to-[#7A0F1A] text-white shadow-lg border-t border-red-800 md:hidden">
      <div className="px-4 py-3 flex items-start gap-3">
        <div className="flex-shrink-0 mt-0.5">
          <Smartphone className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-sm mb-1">Use the RollOps App</h3>
          <p className="text-xs text-red-100 mb-3">
            Get the full experience with all features optimized for mobile
          </p>
          <div className="flex flex-col gap-2">
            <button
              onClick={handleOpenApp}
              className="bg-white text-rollops-primary px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-100 transition-colors text-center"
            >
              Open in App
            </button>
            <div className="flex gap-2">
              {isIOS && (
                <button
                  onClick={handleDownloadIOS}
                  className="flex-1 bg-gray-800 text-white px-3 py-1.5 rounded text-xs font-medium hover:bg-gray-700 transition-colors"
                >
                  Download iOS
                </button>
              )}
              {isAndroid && (
                <button
                  onClick={handleDownloadAndroid}
                  className="flex-1 bg-gray-800 text-white px-3 py-1.5 rounded text-xs font-medium hover:bg-gray-700 transition-colors"
                >
                  Download Android
                </button>
              )}
              {!isIOS && !isAndroid && (
                <>
                  <button
                    onClick={handleDownloadIOS}
                    className="flex-1 bg-gray-800 text-white px-3 py-1.5 rounded text-xs font-medium hover:bg-gray-700 transition-colors"
                  >
                    iOS
                  </button>
                  <button
                    onClick={handleDownloadAndroid}
                    className="flex-1 bg-gray-800 text-white px-3 py-1.5 rounded text-xs font-medium hover:bg-gray-700 transition-colors"
                  >
                    Android
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
        <button
          onClick={handleDismiss}
          className="flex-shrink-0 text-white hover:text-gray-200 transition-colors p-1"
          aria-label="Dismiss banner"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

